import React from 'react';
import { useNavigate } from 'react-router-dom';
import TopNav from '../components/TopNav';
import SocialFooter from '../components/SocialFooter';
import { ArrowLeft, Warning, CalendarX, CreditCard, ShieldWarning, Gavel, Scales } from '@phosphor-icons/react';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_cardio-journey-app/artifacts/tyj7q2ct_logo%20365.png";

const RefundPolicy = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-background relative overflow-hidden" data-testid="refund-policy-screen">
      {/* Watermark - bigger, positioned above the boxes */}
      <div
        className="absolute inset-0 opacity-[0.08] pointer-events-none"
        style={{
          backgroundImage: `url(${LOGO_URL})`,
          backgroundPosition: 'center top 60px',
          backgroundRepeat: 'no-repeat',
          backgroundSize: '80%',
        }}
      />

      <TopNav showHome={true} />

      <main className="max-w-3xl mx-auto px-6 py-8 relative z-10">
        {/* Header with logo on LEFT, bigger, closer to title */}
        <div className="flex items-center gap-4 mb-4">
          <button onClick={() => navigate(-1)} className="w-10 h-10 rounded-full bg-card flex items-center justify-center flex-shrink-0" data-testid="back-btn">
            <ArrowLeft weight="bold" className="w-5 h-5 text-foreground" />
          </button>
          <img src={LOGO_URL} alt="Track365" className="w-40 h-auto flex-shrink-0" data-testid="refund-logo" />
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-[#D4AF37]" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Refund & Billing Policy
            </h1>
            <p className="text-sm text-muted-foreground">Effective Date: March 26, 2026</p>
          </div>
        </div>

        {/* No Chargebacks Warning */}
        <div className="bg-red-500/10 border border-red-500/20 rounded-xl px-4 py-3 mb-6 flex items-center gap-3" data-testid="no-chargebacks-banner">
          <ShieldWarning weight="fill" className="w-5 h-5 text-red-400 flex-shrink-0" />
          <p className="text-sm text-red-400 font-semibold">No Chargebacks. Contact us before initiating any dispute.</p>
        </div>

        <p className="text-muted-foreground mb-8">
          Thank you for choosing Track365. This policy outlines how subscriptions, payments, cancellations, and refunds are handled.
        </p>

        <div className="space-y-6">
          <div className="premium-card p-5">
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 rounded-xl bg-[#D4AF37]/10 flex items-center justify-center flex-shrink-0">
                <CreditCard weight="duotone" className="w-5 h-5 text-[#D4AF37]" />
              </div>
              <div>
                <h3 className="text-foreground font-semibold mb-2">1. Subscription Terms</h3>
                <ul className="text-sm text-muted-foreground space-y-1">
                  <li>- Subscriptions automatically renew at the end of each billing cycle</li>
                  <li>- You authorize us to charge your payment method on a recurring basis</li>
                  <li>- Pricing and features may vary depending on your selected plan</li>
                </ul>
              </div>
            </div>
          </div>

          <div className="premium-card p-5">
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 rounded-xl bg-green-500/10 flex items-center justify-center flex-shrink-0">
                <CalendarX weight="duotone" className="w-5 h-5 text-green-500" />
              </div>
              <div>
                <h3 className="text-foreground font-semibold mb-2">2. Free Trial (If Applicable)</h3>
                <ul className="text-sm text-muted-foreground space-y-1">
                  <li>- You will not be charged during the trial period</li>
                  <li>- Cancel anytime before trial ends to avoid being charged</li>
                  <li>- If not canceled, subscription begins automatically at trial end</li>
                </ul>
              </div>
            </div>
          </div>

          <div className="premium-card p-5 border-red-500/20">
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 rounded-xl bg-red-500/10 flex items-center justify-center flex-shrink-0">
                <Warning weight="duotone" className="w-5 h-5 text-red-500" />
              </div>
              <div>
                <h3 className="text-foreground font-semibold mb-2">3. Refund Policy</h3>
                <p className="text-sm text-red-400 font-medium mb-2">All purchases are final and non-refundable, except as required by law.</p>
                <p className="text-sm text-muted-foreground mb-2">We do not provide refunds for:</p>
                <ul className="text-sm text-muted-foreground space-y-1">
                  <li>- Partial billing periods</li>
                  <li>- Unused time on a subscription</li>
                  <li>- Failure to cancel before the renewal date</li>
                </ul>
              </div>
            </div>
          </div>

          <div className="premium-card p-5">
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 rounded-xl bg-[#D4AF37]/10 flex items-center justify-center flex-shrink-0">
                <Scales weight="duotone" className="w-5 h-5 text-[#D4AF37]" />
              </div>
              <div>
                <h3 className="text-foreground font-semibold mb-2">4. Cancellation Policy</h3>
                <ul className="text-sm text-muted-foreground space-y-1">
                  <li>- You may cancel your subscription at any time</li>
                  <li>- Cancellation stops future billing</li>
                  <li>- Access continues until end of current billing period</li>
                  <li>- No refunds for remaining time after cancellation</li>
                </ul>
              </div>
            </div>
          </div>

          <div className="premium-card p-5 border-red-500/20">
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 rounded-xl bg-red-500/10 flex items-center justify-center flex-shrink-0">
                <ShieldWarning weight="duotone" className="w-5 h-5 text-red-500" />
              </div>
              <div>
                <h3 className="text-red-400 font-semibold mb-2">5. No Chargebacks</h3>
                <p className="text-sm text-muted-foreground mb-2">If you initiate a chargeback or dispute:</p>
                <ul className="text-sm text-muted-foreground space-y-1">
                  <li>- Your account may be suspended or terminated</li>
                  <li>- Access to services may be revoked immediately</li>
                  <li>- We reserve the right to dispute fraudulent chargebacks</li>
                </ul>
                <p className="text-sm text-[#D4AF37] mt-2 font-medium">Please contact us before initiating a dispute so we can resolve the issue.</p>
              </div>
            </div>
          </div>

          <div className="premium-card p-5">
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 rounded-xl bg-[#D4AF37]/10 flex items-center justify-center flex-shrink-0">
                <Gavel weight="duotone" className="w-5 h-5 text-[#D4AF37]" />
              </div>
              <div>
                <h3 className="text-foreground font-semibold mb-2">6. Fraud Prevention</h3>
                <ul className="text-sm text-muted-foreground space-y-1">
                  <li>- We reserve the right to refuse or cancel any transaction</li>
                  <li>- Additional verification may be required for certain payments</li>
                  <li>- Suspicious activity may result in account suspension</li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        <div className="mt-8 pt-6 border-t border-border text-center">
          <p className="text-sm text-muted-foreground mb-2">Questions about billing or this policy?</p>
          <a href="mailto:support@track365cardio.com" className="text-[#D4AF37] hover:underline">
            support@track365cardio.com
          </a>
        </div>

        <SocialFooter />
      </main>
    </div>
  );
};

export default RefundPolicy;
